import { useEffect, useState, type FormEvent } from 'react';
import { api, type NewToken, type TokenEntry } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { ClientsSection } from './ClientsSection';

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; tokens: TokenEntry[] };

export function TokenSettings() {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<NewToken | null>(null);
  const [newName, setNewName] = useState('');

  async function refresh() {
    setState({ kind: 'loading' });
    try {
      const tokens = await api.listTokens();
      setState({ kind: 'ready', tokens });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      const token = await api.createToken(newName.trim() || null);
      setRevealed(token);
      setNewName('');
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(entry: TokenEntry) {
    const ok = window.confirm(
      `Revoke "${entry.name ?? entry.id}"? Devices using it will be logged out.`
    );
    if (!ok) return;
    try {
      await api.revokeToken(entry.id);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Revoke failed');
    }
  }

  return (
    <div>
      <div className="mb-8">
        <div className="mono-label text-black/50 mb-4">account settings</div>
        <h1 className="font-display text-section font-medium tracking-section text-black">
          Tokens
        </h1>
        <p className="mt-3 font-display text-body-lg text-black/60 max-w-2xl tracking-body">
          Each device that syncs uses its own token. Create one per laptop/server you want to
          sync from, revoke when you lose access or rotate credentials.
        </p>
      </div>

      <section className="card p-5 mb-8">
        <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label htmlFor="token-name" className="block mono-label text-black/50 mb-2">
              Create new token
            </label>
            <input
              id="token-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="laptop-macbook-air"
              maxLength={64}
              className="w-full bg-glass-dark border border-border-light rounded-sharp px-3 py-2.5 font-mono text-sm text-black placeholder-black/30 outline-none focus:border-midnight transition-colors"
            />
          </div>
          <button type="submit" disabled={creating} className="btn-primary">
            {creating ? 'Creating…' : 'Create token'}
          </button>
        </form>
      </section>

      {revealed && <RevealedToken token={revealed} onDismiss={() => setRevealed(null)} />}

      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-sub font-medium tracking-sub text-black">
            Active tokens
          </h2>
          {state.kind === 'ready' && (
            <span className="mono-label text-black/50">
              {state.tokens.filter((t) => !t.revoked_at).length} active /{' '}
              {state.tokens.length} total
            </span>
          )}
        </div>

        {state.kind === 'loading' && (
          <div className="py-8 flex items-center justify-center">
            <span className="mono-label text-black/50">loading…</span>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="card p-5">
            <p className="font-display text-body text-black/70">
              Could not load tokens: {state.message}
            </p>
          </div>
        )}

        {state.kind === 'ready' && (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-glass-dark">
                <tr>
                  <Th>Name</Th>
                  <Th>Id</Th>
                  <Th>Created</Th>
                  <Th>Last used</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light">
                {state.tokens.map((token) => (
                  <tr key={token.id} className="hover:bg-glass-dark/50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-display text-body font-medium">
                          {token.name ?? <em className="text-black/40">(unnamed)</em>}
                        </span>
                        {token.is_current && <span className="badge">current</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-[11px] text-black/60">
                      {token.id}
                    </td>
                    <td className="px-5 py-3 font-mono text-[11px] text-black/60">
                      {formatDateTime(token.created_at)}
                    </td>
                    <td className="px-5 py-3 font-mono text-[11px] text-black/60">
                      {token.last_used_at ? formatDateTime(token.last_used_at) : '—'}
                    </td>
                    <td className="px-5 py-3">
                      {token.revoked_at ? (
                        <span className="badge !bg-[#fee2e2] !border-[#fca5a5] !text-[#b91c1c]">
                          revoked
                        </span>
                      ) : (
                        <span className="badge !bg-[#dcfce7] !border-[#86efac]">active</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {!token.revoked_at && !token.is_current && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(token)}
                          className="font-display text-caption text-[#b91c1c] hover:underline"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ClientsSection />
    </div>
  );
}

function RevealedToken({ token, onDismiss }: { token: NewToken; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <section className="card-dark p-5 mb-8">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="mono-label text-white/60 mb-3">new token — shown once</div>
          <p className="font-display text-body-lg text-white mb-3 tracking-body">
            {token.name ?? 'Unnamed token'} was created. Copy it now and store it securely —
            the secret is never shown again.
          </p>
          <pre className="bg-glass-light rounded-sharp px-3 py-2 font-mono text-[12px] text-white overflow-x-auto">
            {token.token}
          </pre>
        </div>
        <button
          type="button"
          onClick={copy}
          className="btn-glass shrink-0"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={onDismiss}
          className="font-display text-caption text-white/60 hover:text-white transition-colors"
        >
          Dismiss
        </button>
      </div>
    </section>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`mono-label text-black/50 text-left px-5 py-3 ${className ?? ''}`}>
      {children}
    </th>
  );
}
